/**
 * Sibling-group strip for the dynamic exception route - auto-derives a
 * synthetic card strip from `siblingCodes.ts` so cross-system sibling
 * exceptions (e.g. the LS/Xero duplicate pair) get a switcher above the
 * filters. Each card carries the same chrome as any other IssueCardStrip
 * card, but `applySelection` navigates to a different code's URL instead
 * of writing the current URL's params - the strip is logically a "switch
 * to a different code", not a filter.
 *
 * Extracted from `DynamicExceptionRoute.tsx` as a pure structural move -
 * state, effect and memos are byte-identical to the inline originals.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getSiblingGroupForCode,
  SiblingGroup,
} from '../config/exceptions/siblingCodes'
import inspectorApi from '../services/inspector/api'
import { generateExceptionUrl } from '../utils/urls'
import type {
  ExceptionConfig,
  ExceptionFilterStripDef,
} from '../config/exceptions/types'

export function useSiblingStrip({
  fpCode,
  tenureId,
  exceptionCode,
  config,
}: {
  fpCode?: string
  tenureId?: number
  exceptionCode?: string
  config: ExceptionConfig | null
}) {
  const navigate = useNavigate()

  const siblingGroup = useMemo<SiblingGroup | null>(
    () =>
      // Configs that already declare their own filter strips opt out -
      // sales-compare's mismatch axis is mutually exclusive with any
      // sibling-code switcher.
      (config?.filterStrips?.length ?? 0) > 0
        ? null
        : getSiblingGroupForCode(exceptionCode),
    [config, exceptionCode],
  )

  const [siblingCounts, setSiblingCounts] = useState<Record<string, number>>({})
  useEffect(() => {
    if (!fpCode || !siblingGroup) {
      setSiblingCounts({})
      return
    }
    let cancelled = false
    inspectorApi
      .getExceptionTypes(siblingGroup.domainKey, fpCode)
      .then((res) => {
        if (cancelled) return
        const types = res?.exception_types || []
        const next: Record<string, number> = {}
        for (const t of types) {
          if (typeof t?.code === 'string' && typeof t?.count === 'number') {
            next[t.code] = t.count
          }
        }
        setSiblingCounts(next)
      })
      .catch((err: any) => {
        console.error('Failed to fetch sibling-group counts:', err)
      })
    return () => {
      cancelled = true
    }
  }, [fpCode, siblingGroup])

  const siblingStrip = useMemo<ExceptionFilterStripDef | null>(() => {
    if (!siblingGroup || !fpCode) return null
    const cards = siblingGroup.codes.map((m) => ({
      key: m.code,
      label: m.label,
      count: siblingCounts[m.code] ?? null,
      icon: m.icon,
    }))
    return {
      type: 'cards',
      buildCards: () => cards,
      getActiveKey: () => exceptionCode ?? null,
      applySelection: (params, key) => {
        if (key && key !== exceptionCode) {
          navigate(generateExceptionUrl(fpCode, siblingGroup.domainKey, key, tenureId))
        }
        // Return params unchanged - the navigation re-mounts the
        // route, no URL-param mutation needed.
        return new URLSearchParams(params)
      },
    }
  }, [siblingGroup, siblingCounts, exceptionCode, fpCode, tenureId, navigate])

  return { siblingStrip }
}
